import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from 'react-leaflet';
import { Icon, DivIcon } from 'leaflet';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Fab,
  Tooltip,
  useTheme,
  Divider
} from '@mui/material';
import { Add, MyLocation } from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip, ResponsiveContainer } from 'recharts';
import { mapService, stationTimeseriesService } from '../services/api';
import { SUPPORTED_METRICS } from '../config/sensorMetrics';
import 'leaflet/dist/leaflet.css';

// Custom styles for transparent popup
const popupStyles = `
  .leaflet-popup-content-wrapper,
  .leaflet-popup-tip {
    background: transparent !important;
    box-shadow: none !important;
  }
`;

// Fix for default markers in react-leaflet
delete (Icon.Default.prototype as any)._getIconUrl;
Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

interface Location {
  latitude: number;
  longitude: number;
}

interface Station {
  id?: number | string;  // Database ID for public stations, or UUID string
  uuid?: string;  // UUID for owned stations
  name: string;
  description?: string;
  location: Location;
  elevation?: number;
  status: string;
  debug: boolean;
  latest_readings?: Record<string, any>;
  owner?: string;
  public: boolean;
}

// Use centralized metric configuration
const metricConfig: Record<string, { name: string; color: string; unit: string }> = (() => {
  const config: Record<string, { name: string; color: string; unit: string }> = {};
  SUPPORTED_METRICS.forEach(metric => {
    config[metric.internal] = {
      name: metric.userFriendly.charAt(0).toUpperCase() + metric.userFriendly.slice(1).replace(/_/g, ' '),
      color: metric.color,
      unit: metric.unit
    };
  });
  return config;
})();

// Station Popup Component with charts
const StationPopupContent: React.FC<{ station: Station; isDarkMode: boolean; onViewDetails: () => void }> = ({
  station,
  isDarkMode,
  onViewDetails
}) => {
  const [timeSeriesData, setTimeSeriesData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchTimeSeriesData();
  }, [station.uuid || station.id]);

  const fetchTimeSeriesData = async () => {
    setLoading(true);
    try {
      // Use uuid if available (owned station), otherwise use id (public station)
      const stationIdentifier = (station.uuid || station.id) as string;
      const data = await stationTimeseriesService.getTimeseries(stationIdentifier, 24);
      const formattedData = data.timeseries.map((point: any) => ({
        ...point,
        time: new Date(point.timestamp).toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit'
        })
      }));
      setTimeSeriesData(formattedData);
    } catch (error) {
      console.error('Error fetching time series data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getAvailableMetrics = () => {
    if (timeSeriesData.length === 0) return [];
    const firstPoint = timeSeriesData[0];
    return Object.keys(firstPoint).filter(
      key => key !== 'timestamp' && key !== 'time' && firstPoint[key] !== null && firstPoint[key] !== undefined
    );
  };

  return (
    <Card elevation={0} sx={{ backgroundColor: 'transparent' }}>
      <CardContent sx={{
        p: 2,
        '&:last-child': { pb: 2 },
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)',
        borderRadius: 2,
        ...(isDarkMode && {
          backgroundColor: 'rgba(30, 30, 30, 0.95)',
          color: 'white'
        })
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="h6" gutterBottom>
              {station.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {station.description}
            </Typography>
          </Box>
          <Chip
            label={station.status}
            size="small"
            color={station.status === 'Online' ? 'success' : station.status === 'Maintenance' ? 'warning' : 'error'}
          />
        </Box>

        <Typography variant="body2" color="text.secondary" gutterBottom>
          📍 {station.location?.latitude?.toFixed(4) || 'N/A'}, {station.location?.longitude?.toFixed(4) || 'N/A'}
          {station.elevation && ` (${station.elevation}m)`}
        </Typography>

        {station.owner && (
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Owner: {station.owner}
          </Typography>
        )}

        {station.debug && (
          <Chip icon={<span>🔧</span>} label="Debug Mode" size="small" color="info" sx={{ mb: 2 }} />
        )}

        {station.latest_readings && Object.keys(station.latest_readings).length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Latest Readings:
            </Typography>
            {Object.entries(station.latest_readings).slice(0, 3).map(([sensor, reading]: [string, any]) => (
              <Typography key={sensor} variant="body2" color="text.secondary">
                {metricConfig[sensor]?.name || sensor}: {reading.value} {reading.unit}
              </Typography>
            ))}
          </Box>
        )}

        {!loading && timeSeriesData.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>
              24-Hour Trends:
            </Typography>
            {getAvailableMetrics().slice(0, 2).map((metric) => {
              const config = metricConfig[metric];
              if (!config) return null;

              return (
                <Box key={metric} sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    {config.name}
                  </Typography>
                  <ResponsiveContainer width="100%" height={100}>
                    <LineChart data={timeSeriesData}>
                      <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#444' : '#ddd'} />
                      <XAxis
                        dataKey="time"
                        tick={{ fontSize: 10, fill: isDarkMode ? '#aaa' : '#666' }}
                        interval="preserveStartEnd"
                      />
                      <YAxis
                        tick={{ fontSize: 10, fill: isDarkMode ? '#aaa' : '#666' }}
                        width={35}
                      />
                      <ChartTooltip
                        contentStyle={{
                          backgroundColor: isDarkMode ? '#333' : '#fff',
                          border: '1px solid #555',
                          borderRadius: '4px',
                          fontSize: '11px'
                        }}
                      />
                      <Line
                        type="monotone"
                        dataKey={metric}
                        stroke={config.color}
                        strokeWidth={2}
                        dot={false}
                        name={`${config.name} (${config.unit})`}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              );
            })}
          </>
        )}

        <Button
          fullWidth
          variant="outlined"
          size="small"
          sx={{ mt: 2 }}
          onClick={onViewDetails}
        >
          View Full Details
        </Button>
      </CardContent>
    </Card>
  );
};

interface MapViewProps {
  onStationSelect?: (stationUuid: string) => void;
  onAddStation?: (location: { lat: number; lng: number }) => void;
}

// Component to recenter map
const RecenterMap: React.FC<{ center: [number, number] }> = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    map.setView(center, map.getZoom());
  }, [map, center]);
  return null;
};

// Component to handle map clicks for adding stations
const MapClickHandler: React.FC<{ onAddStation?: (location: { lat: number; lng: number }) => void }> = ({ onAddStation }) => {
  useMapEvents({
    click: (e) => {
      if (onAddStation) {
        const { lat, lng } = e.latlng;
        console.log('Map clicked at:', { lat, lng }); // Debug log
        onAddStation({ lat, lng });
      }
    },
  });
  return null;
};

const createCustomIcon = (status: string, debug: boolean) => {
  const color = status === 'Online' ? '#4caf50' : 
                status === 'Maintenance' ? '#ff9800' : '#f44336';
  
  const debugIcon = debug ? '🔧' : '';
  
  return new DivIcon({
    html: `
      <div style="
        background-color: ${color};
        width: 25px;
        height: 25px;
        border-radius: 50%;
        border: 3px solid white;
        box-shadow: 0 2px 6px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 12px;
        position: relative;
      ">
        ${debugIcon}
        <div style="
          position: absolute;
          bottom: -8px;
          left: 50%;
          transform: translateX(-50%);
          width: 0;
          height: 0;
          border-left: 6px solid transparent;
          border-right: 6px solid transparent;
          border-top: 8px solid ${color};
        "></div>
      </div>
    `,
    className: 'custom-marker',
    iconSize: [25, 35],
    iconAnchor: [12, 35],
  });
};

const MapView: React.FC<MapViewProps> = ({ onStationSelect, onAddStation }) => {
  const theme = useTheme();
  const [stations, setStations] = useState<Station[]>([]);
  const [loading, setLoading] = useState(true);
  const [mapCenter, setMapCenter] = useState<[number, number]>([40.7128, -74.0060]); // NYC
  const [userLocation, setUserLocation] = useState<[number, number] | null>(null);

  // Determine tile layer based on theme
  const isDarkMode = theme.palette.mode === 'dark';
  const tileLayerUrl = isDarkMode
    ? "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
    : "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png";

  useEffect(() => {
    fetchStations();
    getCurrentLocation();

    // Inject custom popup styles
    const styleElement = document.createElement('style');
    styleElement.textContent = popupStyles;
    document.head.appendChild(styleElement);

    return () => {
      // Cleanup styles on unmount
      if (styleElement.parentNode) {
        styleElement.parentNode.removeChild(styleElement);
      }
    };
  }, []);

  const fetchStations = async () => {
    try {
      const data = await mapService.getStations();
      setStations(data);
      
      // Center map on first station if available
      if (data.length > 0) {
        const firstStation = data[0];
        if (firstStation.location?.latitude && firstStation.location?.longitude) {
          setMapCenter([firstStation.location.latitude, firstStation.location.longitude]);
        }
      }
    } catch (error) {
      console.error('Error fetching stations:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setUserLocation([latitude, longitude]);
        },
        (error) => {
          console.log('Geolocation error:', error);
        }
      );
    }
  };

  const handleMyLocation = () => {
    if (userLocation) {
      setMapCenter(userLocation);
    } else {
      getCurrentLocation();
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Typography>Loading map...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', height: '600px', borderRadius: 2, overflow: 'visible' }}>
      <MapContainer
        center={mapCenter}
        zoom={10}
        style={{ height: '100%', width: '100%' }}
        attributionControl={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors | &copy; <a href="https://cartodb.com/">CartoDB</a>'
          url={tileLayerUrl}
          subdomains="abcd"
          maxZoom={19}
        />
        
        <RecenterMap center={mapCenter} />

        {/* User location marker */}
        {userLocation && (
          <Marker 
            position={userLocation}
            icon={new DivIcon({
              html: `
                <div style="
                  background-color: #2196f3;
                  width: 15px;
                  height: 15px;
                  border-radius: 50%;
                  border: 3px solid white;
                  box-shadow: 0 0 10px rgba(33, 150, 243, 0.8);
                "></div>
              `,
              iconSize: [15, 15],
              iconAnchor: [8, 8],
            })}
          >
            <Popup>
              <Typography variant="body2">
                <strong>Your Location</strong>
              </Typography>
            </Popup>
          </Marker>
        )}

        {/* Station markers */}
        {stations.filter(station => station.location?.latitude && station.location?.longitude).map((station) => (
          <Marker
            key={station.uuid || station.id}
            position={[station.location?.latitude || 0, station.location?.longitude || 0]}
            icon={createCustomIcon(station.status, station.debug)}
          >
            <Popup maxWidth={400} minWidth={350}>
              <StationPopupContent
                station={station}
                isDarkMode={isDarkMode}
                onViewDetails={() => onStationSelect && onStationSelect((station.uuid || station.id) as string)}
              />
            </Popup>
          </Marker>
        ))}
      </MapContainer>

      {/* Floating Action Buttons */}
      <Box sx={{ position: 'absolute', bottom: 16, right: 16, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Tooltip title="My Location" placement="left">
          <Fab size="small" color="primary" onClick={handleMyLocation}>
            <MyLocation />
          </Fab>
        </Tooltip>
        
      </Box>

      {/* Map Legend */}
      <Box sx={{
        position: 'absolute',
        top: 16,
        left: 16,
        bgcolor: 'background.paper',
        p: 2,
        borderRadius: 1,
        boxShadow: 2,
        minWidth: 200
      }}>
        <Typography variant="subtitle2" gutterBottom>
          Station Status
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
          <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#4caf50', mr: 1 }} />
          <Typography variant="caption">Online</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
          <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#ff9800', mr: 1 }} />
          <Typography variant="caption">Maintenance</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
          <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#f44336', mr: 1 }} />
          <Typography variant="caption">Offline</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <span style={{ marginRight: 8, fontSize: '12px' }}>🔧</span>
          <Typography variant="caption">Debug Mode</Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default MapView;